import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { scrubBrowserEvent, scrubBreadcrumb } from './utils/sentryScrub';
import App from './App';
import { RouterProvider } from '@tanstack/react-router';
import { createAppRouter } from './routes/router';
import { installFocusOnNavigate } from './routes/focusOnNavigate';
import { installPageTitle } from './routes/pageTitle';
import { storageAuth } from './routes/auth';
import { TenantProvider } from './contexts/TenantContext';
import { CompanyProvider } from './contexts/CompanyContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from '@/components/ui/sonner';
import './index.css';
import './themes.css';
import { installSelfHeal } from './utils/selfHeal';
import { isRootSiteHost } from './utils/rootSite';
import RootSite from './RootSite';

// Recover open tabs from missing chunks after a deploy (guarded single reload).
installSelfHeal();

// Initialize Sentry error tracking (production only, requires VITE_SENTRY_DSN)
if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
  // Toggle session replay on errors via VITE_SENTRY_REPLAY=true (default: off)
  const replayEnabled = import.meta.env.VITE_SENTRY_REPLAY === 'true';
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: `bc-portal@${import.meta.env.VITE_VERSION || '0.0.0'}`,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Replay is included but both rates default to 0; set VITE_SENTRY_REPLAY=true to enable
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    // No IP address; one-time link secrets (?token=, ?verify=, ?auth_transfer=) redacted (utils/sentryScrub.ts).
    sendDefaultPii: false,
    beforeSend: (event) => scrubBrowserEvent(event),
    beforeBreadcrumb: (crumb) => scrubBreadcrumb(crumb),
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,                          // never record full sessions
    replaysOnErrorSampleRate: replayEnabled ? 1.0 : 0,   // capture replay on every error when enabled
  });
}

/**
 * Provider hierarchy:
 * 1. TenantProvider - Extracts tenant from subdomain (e.g., goodstuff365.bcportal.app)
 * 2. CompanyProvider - Extracts company from URL path (e.g., /Cronus/dashboard)
 * 3. ThemeProvider - Handles light/dark theme
 * 4. RouterProvider - URL routing (routes/router.tsx); App is the root route's component
 */
const root = ReactDOM.createRoot(document.getElementById('root')!);

if (isRootSiteHost(window.location.hostname)) {
  // bcportal.app itself is not a portal: a neutral "Sign in to your portal" page (and /legal/{slug}).
  root.render(
    <React.StrictMode>
      <RootSite />
    </React.StrictMode>
  );
} else {
  const router = createAppRouter({ rootComponent: App, auth: storageAuth });
  installFocusOnNavigate(router);
  installPageTitle(router);

  root.render(
    <React.StrictMode>
      <TenantProvider>
        <CompanyProvider>
          <ThemeProvider>
            <RouterProvider router={router} />
            <Toaster position="top-right" richColors closeButton />
          </ThemeProvider>
        </CompanyProvider>
      </TenantProvider>
    </React.StrictMode>
  );
}
